/* @flow */
import path from 'path';
import { Observable } from 'rxjs';
import { browserHistory } from 'react-router';
import moment from 'moment-timezone';
import { combineEpics } from 'redux-observable';
import isString from 'lodash.isstring';
import { Map, OrderedMap, fromJS, List} from 'immutable';
import createDebugger from 'debug';
import { toString, prop, path as safePath } from 'ramda';

const debug = createDebugger('app:modules:redaction:epics'); // eslint-disable-line no-unused-vars

import { applyRedaction } from './getters.js';
import * as Profile from 'modules/profile';
import { currentReportOrListItem, reportOrListItem } from 'modules/reports/getters.js';
import { bytesToMb } from 'trustar/utils/utils.js';
import { isObject } from 'trustar/utils';
import {
  fetchAndStore as fetchAndStoreReport,
} from 'modules/reports/actions.js';
import { STORE_SUCCESS as STORE_REPORT_SUCCESS } from 'modules/reports/action-types.js';
import { warn, error, success } from 'modules/alerts/actions.js';
import { updateField } from './actions';
import { formValues } from './getters';
import {
  INDICATORS_ENDPOINT,
  INDICATORS_FILE_ENDPOINT,
  UPLOAD_ENDPOINT,
  SUBMIT_ENDPOINT,
  JAVA_DATE_FORMAT,
} from './constants.js';
import {
  REVIEW_REPORT,
  REVIEW_REPORT_SUCCESS,
  REVIEW_REPORT_FAILURE,
  SUBMIT_REPORT,
  SUBMIT_REPORT_SUCCESS,
  SUBMIT_REPORT_FAILURE,
  SUBMIT_INDICATORS,
  SUBMIT_INDICATORS_SUCCESS,
  SUBMIT_INDICATORS_FAILURE,
  SUBMIT_INDICATORS_FILE,
  UPDATE_REPORT,
  UPDATE_REPORT_SUCCESS,
  UPDATE_REPORT_FAILURE,
  ATTACH_FILE,
  ATTACH_FILE_FAILURE,
  UPLOAD_FILE,
  UPLOAD_FILE_SUCCESS,
  UPLOAD_FILE_FAILURE,
  ATTACH_INDICATORS_SPREADSHEET_FILE,
  ATTACH_INDICATORS_SPREADSHEET_FILE_FAILURE,
  ATTACH_INDICATORS_SPREADSHEET_FILE_SUCCESS,
  LOAD_EXISTING_REPORT_CONTENT,
  LOAD_REPORT_DATA,
  TOGGLE_REDACTION,
} from './action-types.js';
import {
  toJSON,
  validateReportContent,
  getValidationError,
  validateReportError,
} from './utils.js';

/* Check report content before moving to 'review' stage */
const reviewReportEpic = (action$, store) =>
  action$.ofType(REVIEW_REPORT)
  .mergeMap(({ payload: { report, redirect } }) => {
    const validationError = validateReportContent(report);
    if (validationError) {
      return [
        { type: REVIEW_REPORT_FAILURE },
        error(validationError)
      ];
    } else {
      return Observable.of({ type: REVIEW_REPORT_SUCCESS })
        .do(() => browserHistory.push(redirect));
    }
  }).catch(err => {
    return Observable.of({ type: REVIEW_REPORT_FAILURE });
  });

const invalidSubmissionEpic = (action$) =>
  action$.ofType(SUBMIT_REPORT)
    .filter(getValidationError)
    .map(getValidationError)
    .map(error);

/* Submit IOCs — handles both regular submission and special file submission */
const submitIndicatorsEpic = (action$, store, { ajax }) =>
  action$.ofType(SUBMIT_INDICATORS, SUBMIT_INDICATORS_FILE)
  .map(action => {
    const endpoint = action.type === SUBMIT_INDICATORS
      ? INDICATORS_ENDPOINT : INDICATORS_FILE_ENDPOINT;

    const request = action.payload.update('content', c => c.get('data'));

    return { endpoint, request };
  })
    .mergeMap(x => Observable.of(x)
      .switchMap(({ request, endpoint }) => {
        return ajax.postJSON(endpoint, request)
          .delay(3000) // Artificial delay to allow the server to "work"
          .mergeMap(response => {
            return [
              { type: SUBMIT_INDICATORS_SUCCESS, payload: response, },
              success('Indicator submission received. You will receive an email notification after processing is complete.'),
            ];
          })
          .do(() => browserHistory.push('/browse/iocs'))
          .catch(err => {
            const message = safePath(['xhr', 'response', 'message'], err);
            return Observable.from([
              { type: SUBMIT_INDICATORS_FAILURE, payload: err },
              error(message ? message : 'An error occurred.'),
            ]);
          });
      })
      .catch(err => {
        return Observable.from([
          { type: SUBMIT_INDICATORS_FAILURE, payload: err },
          error('There was an error submitting the indicators.')
        ]);
      })
    );

/* Submit a new report */
const submitReportEpic = (action$, store, { ajax }) =>
  action$.ofType(SUBMIT_REPORT)
    .filter((action) => !getValidationError(action))
    .mergeMap(action => Observable.of(action)
      .mergeMap(({ payload: report }) => {
        const param = report.get('sendNotification') ? 'true' : 'false';
        const url = `${SUBMIT_ENDPOINT}?sendNotification=${param}`;
        const body = toJSON(report, store.getState);
        return ajax.postJSON(url, body)
          .map(({ response: { id, submissionVersion } }) => ({
            type: SUBMIT_REPORT_SUCCESS,
            payload: {id, submissionVersion}
          }))
          .catch(err => {
            const message = safePath(['xhr', 'response', 'message'], err);
            return Observable.from([
              { type: SUBMIT_REPORT_FAILURE },
              error(message ? message : 'An error occurred.'),
            ]);
          });
      })
      .catch(err => {
        return Observable.from([
          { type: SUBMIT_REPORT_FAILURE, payload: err },
          error('There was an error submitting the report')
        ]);
      })
    );

const invalidUpdateEpic = (action$, store) =>
  action$.ofType(UPDATE_REPORT)
    .filter(x => validateReportError(x) || getValidationError(x))
    .map(x => validateReportError(x) || getValidationError(x))
    .map(error);

/* Update a report */
const updateReportEpic = (action$, store, { ajax }) =>
  action$.ofType(UPDATE_REPORT)
    .filter(x => !validateReportError(x) && !getValidationError(x))
    .mergeMap(action => Observable.of(action)
      .mergeMap(({ payload: report }) => {
        const id = report.get('id');
        const param = report.get('sendNotification') ? 'true' : 'false';
        const url = `${SUBMIT_ENDPOINT}/${id}?sendNotification=${param}`;
        const body = toJSON(report, store.getState);
        return ajax.putJSON(url, body)
          .map(({ response: { id, submissionVersion } }) => ({
            type: UPDATE_REPORT_SUCCESS,
            payload: {id, submissionVersion}
          }))
          .catch(err => {
            const message = safePath(['xhr', 'response', 'message'], err);
            return Observable.from([
              { type: UPDATE_REPORT_FAILURE },
              error(message ? message : 'An error occurred.'),
            ]);
          });
      })
      .catch(err => {
        return Observable.from([
          { type: UPDATE_REPORT_FAILURE, payload: err },
          error('There was an error updating the report'),
        ]);
      })
    );

/**
 * NOTES on file upload:
 *
 * NOTE: We are passed an array of files. For now though we only allow the user
 * to upload one.
 *
 * NOTE: This sets the 'content' key of our form values because the user can
 * also manually type in values. The value we are storing in state did not
 * necessarily come from a file.
 *
 * NOTE: We try to tell the server that we are explicitly expecting a string,
 * but at the time of this writing it isn't respecting that header. Which leads
 * us to the next NOTE...
 *
 * NOTE: We guard against a json value being returned and being parsed into an
 * object by axios. We need to ensure that the extracted content is indeed a
 * string.
 */

const validateFileEpic = (action$, store) =>
  action$.ofType(ATTACH_FILE)
    .mergeMap(action => Observable.of(action)
      .mergeMap(({ payload: file }) => {
        if (!file) {
          const message = 'Could not upload file. This type of file may not be supported.';
          throw new Error(message);
        } else {

          const { name, size, type } = file;
          const sizeInMb = bytesToMb(size);

          const data = new FormData();
          data.append('uploadedFile', file);

          if (sizeInMb > 2) {
            // TODO_LOG Raven.captureMessage('User tried to upload a file larger than 2MB for report submission.', { level: 'info' });
            throw new Error('File too large. The max file size is 2MB.');
          }

          return Observable.of({
            type: UPLOAD_FILE,
            payload: { name, size, type, data }
          });
        }
      })
      .catch(err => {
        return Observable.from([
          { type: ATTACH_FILE_FAILURE },
          error(err.message),
        ]);
      })
    ).catch(err => Observable.of({ type: ATTACH_FILE_FAILURE }));

// attach and upload in one epic
const uploadIndicatorsFileEpic = (action$, { getState }, { ajax }) =>
  action$.ofType(ATTACH_INDICATORS_SPREADSHEET_FILE)
    .mergeMap(action => Observable.of(action)
      .map(({ payload: file }) => {
        const { size } = file;
        const sizeInMb = bytesToMb(size);

        // Add any other validation here...
        if (sizeInMb > 2) {
          // TODO_LOG Raven.captureMessage('User tried to upload a file larger than 2MB for IOC submission.', { level: 'info' });
          throw new Error('File too large. The max file size is 2MB.');
        }

        const data = new FormData();
        data.append('uploadedFile', file);
        return data;
      })
      .mergeMap(data => {
        return ajax({
          method: 'POST',
          url: `${UPLOAD_ENDPOINT}/indicators-file`,
          body: data,
          headers: { Accept: '*/*' },
        })
        .map(prop('response'))
        .mergeMap(data => {
          return [
            updateField(['content', 'data'], fromJS(data)),
            { type: ATTACH_INDICATORS_SPREADSHEET_FILE_SUCCESS }
          ];
        })
        .catch(err => {
          const message = safePath(['xhr', 'response', 'message'], err);
          return [
            error(message ? message : 'There was an error uploading the file.'),
            { type: ATTACH_INDICATORS_SPREADSHEET_FILE_FAILURE, payload: err },
          ];
        });
      }).catch(err => Observable.from([
        error(err.message),
        { type: ATTACH_INDICATORS_SPREADSHEET_FILE_FAILURE, payload: err },
      ]))
    );

/**
 * NOTE: The server is returning Content-Type: application/json regardless of
 * the actual content type. So in this case when the content is a string rxjs
 * would use its default responseType (json) to try to parse the response body
 * from the server. This would of course fail since its plain text, leaving
 * res.respones as null. By manually setting responseType to text we get around
 * this issue.
 *
 * See FileExtractionController.java for the server implementation.
 */
const uploadFileEpic = (action$, { getState }, { ajax }) =>
  action$.ofType(UPLOAD_FILE)
    .mergeMap(({ payload: { name, size, type, data } }) => {
      return ajax({
        method: 'POST',
        url: UPLOAD_ENDPOINT,
        body: data,
        headers: { Accept: '*/*' },
        responseType: 'text', // See NOTE
      })
        .mergeMap(({ response }) => {
          // Verify the response is a string
          if (isObject(response) && !isString(response)) {
            try {
              response = JSON.stringify(response, null, 2);
            } catch (err) {
              response = toString(response);
            }
          }

          const action = [];
          const payload = { name, size, type, data: response };
          const currentValues = formValues(getState());

          // Update form content with extracted report content
          action.push(updateField(['content'], fromJS(payload)));

          // Set the title to the filename if there is no title
          if (!currentValues.get('title')) {
            const titleFromFilename = path.basename(name, path.extname(name));
            action.push(updateField(['title'], titleFromFilename));
          }

          return action.concat({ type: UPLOAD_FILE_SUCCESS });

        })
        .catch(err => {
          let text = '';
          let serverResponse = '';
          try {
            serverResponse = JSON.parse(err.response).message;
          }
          catch(err) {
            serverResponse = '';
            console.error(err);
          }

          if (err && err.status === 413) {
            // validateFileEpic should prevent this from happening
            text = 'There was an error uploading the file. The report must contain less than 2000 extracted indicators and be less than 2MB.';
          } else if (serverResponse !== '') {
            text = serverResponse;
          } else if (err && err.message) {
            text = err.message;
          } else {
            text = 'There was an error uploading the file.';
          }

          return Observable.from([
            { type: UPLOAD_FILE_FAILURE },
            error(text)
          ]);
        });
    });

/**
 * NOTE: This function has caused ALL SORTS OF BUGS. It's decreasing dev
 * bandwidth. However, it can be refactored after move to redux so for now it
 * stays, with this odd warning about how we don't support linking directly in
 * to a report update :/
 *
 * NOTE: We have some intertwined data in the same object. It creates a huge
 * amount of complexity overhead and should be refactored, but it pervades the
 * entire app (front and back end) so that will need to come at another time.
 */
const toUpdate = (report: Map<any, any>, distributionType: string, enclaves: Map<any, any>): Map<any, any> => {
  if (!report) {
    console.warn('Updating reports before initially load is not supported.');
    return Map();
  }
  const result = {
    id: report.get('id'),
    title: report.get('title'),
    externalTrackingId: report.get('externalTrackingId'),
    content: { data: report.get('reportBody') },
    distributionType,
    enclaves: distributionType === 'ENCLAVE' ? enclaves.map(x => x.get('id')).toArray() : [], // See NOTE
    sector: report.get('sector'),
    attributes: report.get('attributes', OrderedMap()).map(x => x.get('name')).toList(),
    timeBegan: report.get('timeBeganUnknown')
      ? null
      : moment
        .tz(
          report.get('timeBegan'),
          report.get('timeBeganTimezone'))
        .format(JAVA_DATE_FORMAT),
    tags: report.get('tags', List()).toList().map(tag => tag.get('name'))
  };

  return fromJS(result);
};

/**
 * The idea here is simple enough but the logic has become quite convoluted. We
 * want to "load" the data of a report in to the report creation / edit form so
 * that the user can edit it. Makes sense. But there's no guarantee that the
 * report the user wants to edit is already in state (we could decide to make
 * this assumption in order to greatly sipmplify this logic). So we see if the
 * report is in state. If it is, then we load its data in to the form. If it's
 * not, then we go fetch it from the server and THEN do the same thing. There is
 * certainly a better way to make this happen, but that can come after large
 * refactor.
 */
const loadReportEpic = (action$, store) =>
  action$.ofType(LOAD_REPORT_DATA)
    .map(prop('payload'))
    .mergeMap(({ guid, release, enclaveToCopyTo }) => {
      const state = store.getState();
      const report = reportOrListItem(guid)(state);

      /* User may be in "Redact & Release" phase, in which case 'release' signifies
       * we should set the distribtion to COMMUNITY when populating the form */
      // TODO: Should we remove this piece of code?
      const distributionType = release ? 'COMMUNITY' : 'ENCLAVE';
      const enclaves = report.get('enclaves', OrderedMap());
      const formValues = toUpdate(report, distributionType, enclaves);

      // Note: enclaveToCopyTo implies we are COPYING this entry
      const action = {
        type: LOAD_EXISTING_REPORT_CONTENT,
        payload: { currentValues: formValues, enclaveToCopyTo },
      };

      return report
        ? Observable.of(action)
        : Observable.of(fetchAndStoreReport(guid))
          .merge(
            action$.ofType(STORE_REPORT_SUCCESS)
              .map(prop('payload'))
              .map(report => action)
          );
    });

/* This redaction serves a warning message if you toggle off redaction in the report review page */
const redactionWarningEpic = (action$, store) =>
  action$.ofType(TOGGLE_REDACTION)
    .filter(() => !applyRedaction(store.getState()))
    .mapTo(warn('NOTE: Turning redaction off will submit all the text in the report.'))
    .catch(err => Observable.empty());

export default combineEpics(
  reviewReportEpic,
  invalidSubmissionEpic,
  invalidUpdateEpic,
  submitReportEpic,
  submitIndicatorsEpic,
  updateReportEpic,
  validateFileEpic,
  uploadFileEpic,
  uploadIndicatorsFileEpic,
  loadReportEpic,
  redactionWarningEpic,
);



// WEBPACK FOOTER //
// ./src/app/modules/create/epics.js